import { History, RotateCcw, Sparkles } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import type { SeoHistory, SeoHistoryEntry, SeoMetrics } from "../../types/seo";
import {
	metricCells,
	OutcomeChip,
	pendingExplanation,
	TierChip,
	writtenByLabel,
} from "../flows/seo";
import {
	ConfirmDialog,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtRelative,
	Spinner,
} from "../flows/ui";

const MetricRow = ({
	label,
	hint,
	metrics,
}: {
	label: string;
	hint?: string;
	metrics: SeoMetrics | null;
}) => {
	const cells = metricCells(metrics);
	return (
		<div className="rounded-lg border border-slate-100 bg-slate-50/60 p-2.5">
			<p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
				{label}
			</p>
			{cells.length === 0 ? (
				<p className="mt-1 text-[12px] text-slate-400">{hint ?? "Not yet."}</p>
			) : (
				<div className="mt-1.5 grid grid-cols-4 gap-2">
					{cells.map((cell) => (
						<div key={cell.label}>
							<p className="text-[10px] text-slate-400">{cell.label}</p>
							<p className="text-[13px] font-medium tabular-nums text-slate-700">
								{cell.value}
							</p>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const TextField = ({
	label,
	value,
}: {
	label: string;
	value: string | null;
}) => (
	<div>
		<div className="flex items-baseline justify-between gap-2">
			<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
				{label}
			</span>
			<span className="font-mono text-[10px] tabular-nums text-slate-400">
				{value ? `${value.length} chars` : ""}
			</span>
		</div>
		<p className="mt-0.5 whitespace-pre-wrap border-l-2 border-slate-200 pl-2 text-[12px] leading-relaxed text-slate-700">
			{value ?? <em className="text-slate-400">nothing written</em>}
		</p>
	</div>
);

const Version = ({ entry }: { entry: SeoHistoryEntry }) => (
	<div className="rounded-xl border border-slate-200 p-3">
		<div className="flex flex-wrap items-center gap-2">
			<span className="rounded-md bg-slate-100 px-2 py-0.5 text-[12px] font-semibold text-slate-700">
				v{entry.version}
			</span>
			<TierChip tier={entry.tier} />
			<OutcomeChip outcome={entry.outcome} />
			<span className="text-[11px] text-slate-400">
				written by {writtenByLabel(entry.writtenBy)}
			</span>
			{entry.flowRunId && (
				<a
					href={`/flows/runs/${entry.flowRunId}`}
					className="text-[11px] font-medium text-primary"
				>
					run #{entry.flowRunId}
				</a>
			)}
			<span
				className="ml-auto text-[11px] text-slate-400"
				title={fmtDateTime(entry.writtenAt)}
			>
				{fmtRelative(entry.writtenAt)}
			</span>
		</div>

		{entry.outcome === "pending" && (
			<p className="mt-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[12px] leading-relaxed text-slate-500">
				{pendingExplanation(entry.writtenAt)}
			</p>
		)}

		<div className="mt-3 space-y-2">
			<TextField label="Title" value={entry.title} />
			<TextField label="Description" value={entry.description} />
			<TextField label="Keywords" value={entry.keywords} />
			<TextField label="Intro" value={entry.intro} />
		</div>

		<div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-3">
			<MetricRow
				label="Before this version"
				hint="No Search Console data for the 28 days before the write."
				metrics={entry.metricsBefore}
			/>
			<MetricRow
				label="28 days after"
				hint="Measured 28 days after the write."
				metrics={entry.metricsAfter28}
			/>
			<MetricRow
				label="56 days after"
				hint="Measured 56 days after the write."
				metrics={entry.metricsAfter56}
			/>
		</div>
	</div>
);

export const ProductSeoHistory = ({ productId }: { productId: number }) => {
	const navigate = useNavigate();
	const [items, setItems] = useState<SeoHistoryEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [confirming, setConfirming] = useState<"optimize" | "rollback" | null>(
		null,
	);

	const load = useCallback(async () => {
		setError(null);
		try {
			const data = await fetchJson<SeoHistory>(
				`/flows/seo/history/${productId}`,
			);
			setItems(data.items);
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, [productId]);

	useEffect(() => {
		load();
	}, [load]);

	const optimize = async () => {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			const result = await sendJson<{ runId?: number; error?: string }>(
				`/flows/seo/optimize/${productId}`,
				"POST",
			);
			if (result.error) {
				setError(result.error);
				return;
			}
			setConfirming(null);
			if (result.runId) navigate(`/flows/runs/${result.runId}`);
			else setNotice("Queued, but no run id came back.");
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const rollback = async () => {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			const result = await sendJson<{ version?: number; error?: string }>(
				`/flows/seo/rollback/${productId}`,
				"POST",
			);
			if (result.error) {
				setError(result.error);
				return;
			}
			setConfirming(null);
			setNotice(
				`Rolled back. The previous version's text is now version ${result.version}; nothing was deleted to make it.`,
			);
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const current = items[0];
	const previous = items[1];

	return (
		<div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
			<div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
				<History className="h-4 w-4 text-slate-400" />
				<h2 className="text-sm font-semibold text-slate-700">SEO history</h2>
				<span className="ml-1 text-xs font-normal text-slate-400">
					({items.length})
				</span>
				<div className="ml-auto flex items-center gap-2">
					{previous && (
						<button
							type="button"
							disabled={busy}
							onClick={() => setConfirming("rollback")}
							title="Write the previous version's text back as a new version."
							className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
						>
							<RotateCcw className="h-3.5 w-3.5" /> Roll back
						</button>
					)}
					<button
						type="button"
						disabled={busy}
						onClick={() => setConfirming("optimize")}
						title="Queues this product and starts the rank optimiser for it alone, with a live DataForSEO lookup. That lookup is billed per call."
						className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-brand-hover disabled:opacity-50"
					>
						<Sparkles className="h-3.5 w-3.5" /> Optimize now
					</button>
				</div>
			</div>

			<div className="space-y-3 p-6">
				<ErrorBanner error={error} />
				{notice && (
					<p className="rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
						{notice}
					</p>
				)}

				{loading ? (
					<Spinner />
				) : items.length === 0 ? (
					<Empty>
						This product has never been optimised. Optimize now queues it and
						starts the rank optimiser for it alone; otherwise it waits for the
						tier that picks it up.
					</Empty>
				) : (
					<div className="space-y-3">
						{items.map((entry) => (
							<Version key={entry.id} entry={entry} />
						))}
					</div>
				)}
			</div>

			{confirming === "optimize" && (
				<ConfirmDialog
					title="Optimize now"
					busy={busy}
					confirmLabel="Queue and run"
					onCancel={() => setConfirming(null)}
					onConfirm={optimize}
					body={
						<>
							<p>
								This adds a manual row to the SEO queue and starts the rank
								optimiser for this one product, ahead of everything but drops
								and regressions.
							</p>
							<p>
								It runs with the live fallback on, so it makes a{" "}
								<strong>paid DataForSEO lookup</strong> for this product rather
								than reading the nightly cache. Live is roughly 0.016 USD a
								product against 0.0006 for the queued batch, and the daily cap
								is set in Settings.
							</p>
							<p>
								Nothing is published by this: the write still passes the
								validation and approval nodes in the flow.
							</p>
						</>
					}
				/>
			)}

			{confirming === "rollback" && current && previous && (
				<ConfirmDialog
					title="Roll back to the previous version"
					busy={busy}
					danger
					confirmLabel={`Write v${previous.version}'s text as v${current.version + 1}`}
					onCancel={() => setConfirming(null)}
					onConfirm={rollback}
					body={
						<>
							<p>
								This takes the text from <strong>v{previous.version}</strong>{" "}
								and writes it as a <strong>new version</strong>, v
								{current.version + 1}. It does not delete v{current.version} or
								anything else — the history is append-only, and the version
								counter keeps going up.
							</p>
							<p>
								The current v{current.version} title is “
								{current.title ?? "nothing written"}”. It will be replaced by “
								{previous.title ?? "nothing written"}”.
							</p>
							<p>
								Google will need a crawl before this shows in the results, and
								the new version starts its own 28-day measurement.
							</p>
						</>
					}
				/>
			)}
		</div>
	);
};
